"use client";

import { Heart } from "lucide-react";
import { useCallback } from "react";
import { UseFormReturn } from "react-hook-form";

import { FilterValues } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/filter-schema";
import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import useSignInDrawer from "@/hooks/use-sign-in-drawer";
import { jobFiltersTestIds } from "@/lib/data-test-ids/jobs.data-test-ids";
import { User } from "@/lib/types/auth";

interface SavedSchoolsFilterViewProps {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
}

export function SavedSchoolsFilterView({
  checked,
  onCheckedChange,
  disabled,
}: SavedSchoolsFilterViewProps) {
  return (
    <FormItem>
      <div className="flex items-center justify-between mb-1">
        <FormLabel
          htmlFor="saved-schools"
          className="text-sm font-medium text-ink-emphasis cursor-pointer flex items-center gap-2"
        >
          <Heart className="h-4 w-4 text-ink-subtle" />
          Your saved schools
        </FormLabel>
        <FormControl>
          <Switch
            id="saved-schools"
            checked={checked}
            onCheckedChange={onCheckedChange}
            disabled={disabled}
            aria-label="Your saved schools"
            data-testid={jobFiltersTestIds.SAVED_SCHOOLS_FILTER}
          />
        </FormControl>
      </div>
    </FormItem>
  );
}

interface SavedSchoolsFilterContainerProps {
  form: UseFormReturn<FilterValues>;
  user: User;
}

function SavedSchoolsFilterAuthenticated({
  form,
}: SavedSchoolsFilterContainerProps) {
  return (
    <FormField
      control={form.control}
      name="savedSchools"
      render={({ field }) => (
        <SavedSchoolsFilterView
          checked={field.value || false}
          onCheckedChange={field.onChange}
        />
      )}
    />
  );
}

function SavedSchoolsFilterUnauthenticated({
  form,
}: Omit<SavedSchoolsFilterContainerProps, "user">) {
  const { openDrawer } = useSignInDrawer();

  const handleCheckedChange = useCallback(() => {
    openDrawer("1");
  }, [openDrawer]);

  return (
    <FormField
      control={form.control}
      name="savedSchools"
      render={() => (
        <SavedSchoolsFilterView
          checked={false}
          onCheckedChange={handleCheckedChange}
        />
      )}
    />
  );
}

const SavedSchoolsFilter = withAuthGate(SavedSchoolsFilterAuthenticated, {
  customLoader: <SavedSchoolsFilterView checked={false} disabled />,
  customPaywall: (props) => <SavedSchoolsFilterUnauthenticated {...props} />,
});

export default SavedSchoolsFilter;
